import React, { useState, useEffect } from 'react';
import { Upload, Download, Users, AlertCircle, CheckCircle, FileText, BarChart3 } from 'lucide-react';
import cargaMasivaService from '../services/cargaMasivaService';

const CargaMasivaPersonal = () => {
    const [archivo, setArchivo] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [resumenPersonal, setResumenPersonal] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        cargarResumenPersonal();
    }, []);

    const cargarResumenPersonal = async () => {
        try {
            const data = await cargaMasivaService.getResumenPersonal();
            setResumenPersonal(data);
        } catch (error) {
            setError('Error cargando resumen: ' + error.message);
        }
    };

    const descargarPlantilla = async () => {
        try {
            setCargando(true);
            setError('');
            
            await cargaMasivaService.descargarPlantilla();

        } catch (error) {
            setError('Error descargando plantilla: ' + error.message);
        } finally {
            setCargando(false);
        }
    };

    const manejarSeleccionArchivo = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validar extensión
            if (!file.name.endsWith('.csv')) {
                setError('Por favor seleccione un archivo CSV (.csv)');
                return;
            }
            
            // Validar tamaño (10MB máximo)
            const MAX_SIZE = 10 * 1024 * 1024; // 10MB
            if (file.size > MAX_SIZE) {
                setError(`El archivo excede el tamaño máximo de 10MB. Tamaño actual: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
                return;
            }
            
            setArchivo(file);
            setError('');
            setResultado(null);
        }
    };

    const subirArchivo = async () => {
        if (!archivo) {
            setError('Por favor seleccione un archivo');
            return;
        }

        // Confirmación antes de procesar
        const confirmar = window.confirm(
            '⚠️ ADVERTENCIA IMPORTANTE:\n\n' +
            '• Los empleados que NO estén en el archivo serán DESACTIVADOS\n' +
            '• Los empleados existentes serán ACTUALIZADOS con la nueva información\n' +
            '• Los nuevos empleados serán CREADOS\n\n' +
            '¿Está seguro de que desea continuar con la carga masiva?'
        );
        
        if (!confirmar) {
            return;
        }

        try {
            setCargando(true);
            setError('');
            
            const data = await cargaMasivaService.cargarPersonalMasivo(archivo);

            setResultado(data);
            setArchivo(null);
            // Limpiar input file
            const fileInput = document.getElementById('archivo-input');
            if (fileInput) fileInput.value = '';
            
            // Recargar resumen
            await cargarResumenPersonal();

        } catch (error) {
            setError('Error subiendo archivo: ' + error.message);
        } finally {
            setCargando(false);
        }
    };

    const ResultadoCarga = ({ resultado }) => {
        const { resultados } = resultado;
        const totalProcesados = resultados.lideres_creados + resultados.lideres_actualizados + 
                               resultados.empleados_creados + resultados.empleados_actualizados;

        return (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center mb-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    <h3 className="text-lg font-semibold text-green-800">Carga Completada</h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-blue-600">{resultados.lideres_creados}</div>
                        <div className="text-sm text-gray-600">Líderes Creados</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-green-600">{resultados.lideres_actualizados}</div>
                        <div className="text-sm text-gray-600">Líderes Actualizados</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-blue-600">{resultados.empleados_creados}</div>
                        <div className="text-sm text-gray-600">Empleados Creados</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-green-600">{resultados.empleados_actualizados}</div>
                        <div className="text-sm text-gray-600">Empleados Actualizados</div>
                    </div>
                </div>

                {resultados.empleados_desactivados > 0 && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-yellow-600">{resultados.empleados_desactivados}</div>
                            <div className="text-sm text-yellow-700">Empleados Desactivados (no estaban en el archivo)</div>
                        </div>
                    </div>
                )}

                {resultados.errores && resultados.errores.length > 0 && (
                    <div className="mt-4">
                        <h4 className="font-semibold text-red-800 mb-2">Errores encontrados:</h4>
                        <ul className="list-disc list-inside text-sm text-red-700 bg-red-50 p-3 rounded">
                            {resultados.errores.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="mt-4 text-center">
                    <span className="text-lg font-semibold text-gray-700">
                        Total procesados: {totalProcesados} registros
                    </span>
                </div>
            </div>
        );
    };

    const ResumenPersonal = ({ resumen }) => {
        if (!resumen) return null;

        return (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center mb-3">
                    <BarChart3 className="h-5 w-5 text-blue-600 mr-2" />
                    <h3 className="text-lg font-semibold text-blue-800">Resumen Actual del Personal</h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-blue-600">{resumen.lideres.activos}</div>
                        <div className="text-sm text-gray-600">Líderes Activos</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-green-600">{resumen.empleados.activos}</div>
                        <div className="text-sm text-gray-600">Empleados Activos</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-purple-600">{resumen.total_personal}</div>
                        <div className="text-sm text-gray-600">Total Personal</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-orange-600">{resumen.departamentos.length}</div>
                        <div className="text-sm text-gray-600">Departamentos</div>
                    </div>
                </div>

                {resumen.departamentos.length > 0 && (
                    <div className="mt-4">
                        <h4 className="font-semibold text-gray-700 mb-2">Distribución por Departamentos:</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {resumen.departamentos.slice(0, 6).map((dept, index) => (
                                <div key={index} className="flex justify-between items-center p-2 bg-white rounded border text-sm">
                                    <span className="text-gray-700">{dept.nombre}</span>
                                    <span className="font-semibold text-blue-600">{dept.total}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="sm:flex sm:items-center mb-6">
                <div className="sm:flex-auto">
                    <h1 className="text-2xl font-semibold text-gray-900">Carga Masiva de Personal</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Carga y sincronización masiva de empleados y líderes desde archivo CSV
                    </p>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">

                {/* Resumen Actual */}
                <ResumenPersonal resumen={resumenPersonal} />

                {/* Instrucciones */}
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                        <div>
                            <h3 className="font-semibold text-yellow-800 mb-2">Instrucciones Importantes:</h3>
                            <ul className="text-sm text-yellow-700 space-y-1">
                                <li>• <strong>Descarga la plantilla CSV</strong> y llénala con los datos del personal</li>
                                <li>• <strong>Formato:</strong> CSV separado por comas con encabezados</li>
                                <li>• <strong>Empleados existentes</strong> serán actualizados con la nueva información</li>
                                <li>• <strong>Empleados no incluidos</strong> en el archivo serán desactivados</li>
                                <li>• <strong>Nuevos empleados</strong> serán creados automáticamente</li>
                                <li>• Los líderes deben estar en el archivo antes que sus empleados</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Botón Descargar Plantilla */}
                <div className="mb-6">
                    <button
                        onClick={descargarPlantilla}
                        disabled={cargando}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <Download className="h-5 w-5 mr-2" />
                        {cargando ? 'Generando...' : 'Descargar Plantilla CSV'}
                    </button>
                </div>

                {/* Subir Archivo */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seleccionar archivo CSV con datos del personal:
                    </label>
                    <div className="flex items-center space-x-4">
                        <input
                            id="archivo-input"
                            type="file"
                            accept=".csv"
                            onChange={manejarSeleccionArchivo}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <button
                            onClick={subirArchivo}
                            disabled={!archivo || cargando}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <Upload className="h-5 w-5 mr-2" />
                            {cargando ? 'Procesando...' : 'Subir Archivo'}
                        </button>
                    </div>
                    {archivo && (
                        <p className="mt-2 text-sm text-gray-600">
                            Archivo seleccionado: {archivo.name}
                        </p>
                    )}
                </div>

                {/* Error */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                            <span className="text-red-800">{error}</span>
                        </div>
                    </div>
                )}

                {/* Resultado */}
                {resultado && <ResultadoCarga resultado={resultado} />}

                {/* Loading */}
                {cargando && (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">Procesando...</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CargaMasivaPersonal;